/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Developer API Telemetry & Analytics Store (Task 10.6.2)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import { create } from 'zustand';

export interface TelemetryPoint {
  timestamp: number;
  label: string;
  totalRequests: number;
  successfulRequests: number;
  rateLimitedRequests: number;
  serverErrorRequests: number;
  tokensUsed: number;
  avgLatencyMs: number;
}

export interface EndpointUsageItem {
  id: string;
  name: string;
  endpoint: string;
  tokens: number;
  requests: number;
  percentage: number;
  color: string;
}

export interface HttpStatusBreakdown {
  status200: number;
  status429: number;
  status5xx: number;
  total: number;
  successRate: number;
}

export interface ApiRequestLog {
  id: string;
  timestamp: number;
  keyId: string;
  keyHint: string;
  endpoint: string;
  method: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  statusCode: number;
  latencyMs: number;
  ipAddress: string;
  tokensUsed: number;
  errorMessage?: string;
}

export type TimeRange = '24h' | '7d' | '30d' | '90d';
export type StatusFilter = 'all' | '2xx' | '4xx' | '5xx';

/**
 * Generate 24 hourly telemetry points for the 24h range
 */
function generate24hTelemetry(): TelemetryPoint[] {
  const points: TelemetryPoint[] = [];
  const now = Date.now();
  const oneHour = 3600000;

  for (let i = 23; i >= 0; i--) {
    const time = now - i * oneHour;
    const date = new Date(time);
    const hourStr = `${date.getHours().toString().padStart(2, '0')}:00`;
    
    // Realistic curve: higher requests during daytime (10:00 - 18:00)
    const h = date.getHours();
    const isPeak = h >= 9 && h <= 18;
    const baseReqs = isPeak ? Math.floor(80 + Math.sin(h) * 40) : Math.floor(20 + Math.cos(h) * 15);
    const rateLimited = isPeak && h % 4 === 0 ? Math.floor(Math.random() * 6) + 1 : 0;
    const serverErrors = i === 12 ? 1 : 0;
    const successful = Math.max(0, baseReqs - rateLimited - serverErrors);
    const tokens = baseReqs * Math.floor(350 + Math.random() * 200);
    const latency = Math.floor(45 + Math.random() * 35);

    points.push({
      timestamp: time,
      label: hourStr,
      totalRequests: baseReqs,
      successfulRequests: successful,
      rateLimitedRequests: rateLimited,
      serverErrorRequests: serverErrors,
      tokensUsed: tokens,
      avgLatencyMs: latency,
    });
  }

  return points;
}

/**
 * Generate 7 daily telemetry points for the 7d range
 */
function generate7dTelemetry(): TelemetryPoint[] {
  const points: TelemetryPoint[] = [];
  const now = Date.now();
  const oneDay = 86400000;
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  for (let i = 6; i >= 0; i--) {
    const time = now - i * oneDay;
    const date = new Date(time);
    const label = dayNames[date.getDay()];
    const baseReqs = Math.floor(1800 + Math.random() * 600);
    const rateLimited = Math.floor(baseReqs * 0.02);
    const serverErrors = Math.floor(Math.random() * 4);
    const successful = baseReqs - rateLimited - serverErrors;

    points.push({
      timestamp: time,
      label,
      totalRequests: baseReqs,
      successfulRequests: successful,
      rateLimitedRequests: rateLimited,
      serverErrorRequests: serverErrors,
      tokensUsed: baseReqs * 420,
      avgLatencyMs: Math.floor(52 + Math.random() * 15),
    });
  }

  return points;
}

/**
 * Generate 30 daily telemetry points
 */
function generate30dTelemetry(): TelemetryPoint[] {
  const points: TelemetryPoint[] = [];
  const now = Date.now();
  const oneDay = 86400000;

  for (let i = 29; i >= 0; i -= 2) {
    const time = now - i * oneDay;
    const date = new Date(time);
    const label = `${date.getDate()}/${date.getMonth() + 1}`;
    const baseReqs = Math.floor(2200 + Math.sin(i) * 500);
    const rateLimited = Math.floor(baseReqs * 0.015);
    const serverErrors = Math.floor(Math.random() * 3);
    const successful = baseReqs - rateLimited - serverErrors;

    points.push({
      timestamp: time,
      label,
      totalRequests: baseReqs,
      successfulRequests: successful,
      rateLimitedRequests: rateLimited,
      serverErrorRequests: serverErrors,
      tokensUsed: baseReqs * 450,
      avgLatencyMs: Math.floor(48 + Math.random() * 12),
    });
  }

  return points;
}

/**
 * Generate 50 realistic recent API request logs
 */
export const SEED_RECENT_LOGS: ApiRequestLog[] = [
  {
    id: 'req_01HP91001',
    timestamp: Date.now() - 12000,
    keyId: 'key-01HXYZ001',
    keyHint: 'a1b2',
    endpoint: '/api/v1/chat/completions',
    method: 'POST',
    statusCode: 200,
    latencyMs: 42,
    ipAddress: '113.161.78.22',
    tokensUsed: 640,
  },
  {
    id: 'req_01HP91002',
    timestamp: Date.now() - 35000,
    keyId: 'key-01HXYZ004',
    keyHint: '2041',
    endpoint: '/api/v1/translation/segments',
    method: 'POST',
    statusCode: 200,
    latencyMs: 38,
    ipAddress: '14.162.145.10',
    tokensUsed: 120,
  },
  {
    id: 'req_01HP91003',
    timestamp: Date.now() - 65000,
    keyId: 'key-01HXYZ003',
    keyHint: '89c3',
    endpoint: '/api/v1/media/images/generate',
    method: 'POST',
    statusCode: 200,
    latencyMs: 285,
    ipAddress: '203.113.152.8',
    tokensUsed: 4000,
  },
  {
    id: 'req_01HP91004',
    timestamp: Date.now() - 95000,
    keyId: 'key-01HXYZ001',
    keyHint: 'a1b2',
    endpoint: '/api/v1/chat/completions',
    method: 'POST',
    statusCode: 429,
    latencyMs: 12,
    ipAddress: '113.161.78.22',
    tokensUsed: 0,
    errorMessage: 'Rate limit exceeded: 120 requests/minute',
  },
  {
    id: 'req_01HP91005',
    timestamp: Date.now() - 140000,
    keyId: 'key-01HXYZ002',
    keyHint: 'f4e9',
    endpoint: '/api/v1/text/completions/stream',
    method: 'POST',
    statusCode: 200,
    latencyMs: 56,
    ipAddress: '118.69.182.44',
    tokensUsed: 890,
  },
  {
    id: 'req_01HP91006',
    timestamp: Date.now() - 190000,
    keyId: 'key-01HXYZ001',
    keyHint: 'a1b2',
    endpoint: '/api/v1/voice/tts',
    method: 'POST',
    statusCode: 200,
    latencyMs: 110,
    ipAddress: '113.161.78.22',
    tokensUsed: 350,
  },
  {
    id: 'req_01HP91007',
    timestamp: Date.now() - 250000,
    keyId: 'key-01HXYZ004',
    keyHint: '2041',
    endpoint: '/api/v1/translation/tm/query',
    method: 'GET',
    statusCode: 200,
    latencyMs: 18,
    ipAddress: '14.162.145.10',
    tokensUsed: 50,
  },
  {
    id: 'req_01HP91008',
    timestamp: Date.now() - 320000,
    keyId: 'key-01HXYZ003',
    keyHint: '89c3',
    endpoint: '/api/v1/media/images/upscale',
    method: 'POST',
    statusCode: 200,
    latencyMs: 310,
    ipAddress: '203.113.152.8',
    tokensUsed: 2000,
  },
  {
    id: 'req_01HP91009',
    timestamp: Date.now() - 410000,
    keyId: 'key-01HXYZ002',
    keyHint: 'f4e9',
    endpoint: '/api/v1/chat/completions',
    method: 'POST',
    statusCode: 500,
    latencyMs: 820,
    ipAddress: '118.69.182.44',
    tokensUsed: 0,
    errorMessage: 'Upstream provider connection timeout',
  },
  {
    id: 'req_01HP91010',
    timestamp: Date.now() - 500000,
    keyId: 'key-01HXYZ001',
    keyHint: 'a1b2',
    endpoint: '/api/v1/chat/completions',
    method: 'POST',
    statusCode: 200,
    latencyMs: 48,
    ipAddress: '113.161.78.22',
    tokensUsed: 512,
  },
];

// Populate 40 more items to reach 50 logs
const SAMPLE_ENDPOINTS = [
  { path: '/api/v1/chat/completions', method: 'POST', tokens: 480, baseLatency: 45 },
  { path: '/api/v1/text/completions/stream', method: 'POST', tokens: 720, baseLatency: 52 },
  { path: '/api/v1/translation/segments', method: 'POST', tokens: 160, baseLatency: 35 },
  { path: '/api/v1/media/images/generate', method: 'POST', tokens: 3000, baseLatency: 260 },
  { path: '/api/v1/voice/tts', method: 'POST', tokens: 280, baseLatency: 95 },
  { path: '/api/v1/knowledge/query', method: 'POST', tokens: 350, baseLatency: 60 },
] as const;

for (let i = 11; i <= 50; i++) {
  const ep = SAMPLE_ENDPOINTS[i % SAMPLE_ENDPOINTS.length];
  const is429 = i % 17 === 0;
  const is500 = i % 37 === 0;
  const statusCode = is429 ? 429 : is500 ? 500 : 200;
  const latency = is500 ? 950 : Math.floor(ep.baseLatency + Math.random() * 30);
  const keyHint = ['a1b2', 'f4e9', '89c3', '2041'][i % 4];
  const keyId = ['key-01HXYZ001', 'key-01HXYZ002', 'key-01HXYZ003', 'key-01HXYZ004'][i % 4];

  SEED_RECENT_LOGS.push({
    id: `req_01HP910${i.toString().padStart(2, '0')}`,
    timestamp: Date.now() - i * 90000,
    keyId,
    keyHint,
    endpoint: ep.path,
    method: ep.method,
    statusCode,
    latencyMs: latency,
    ipAddress: `113.161.${i % 255}.${(i * 3) % 255}`,
    tokensUsed: statusCode === 200 ? ep.tokens : 0,
    errorMessage: is429 ? 'Rate limit exceeded' : is500 ? 'Internal server error' : undefined,
  });
}

export interface DeveloperAnalyticsState {
  timeRange: TimeRange;
  selectedKeyId: 'all' | string;
  statusFilter: StatusFilter;
  searchQuery: string;
  isRefreshing: boolean;
  telemetryData: TelemetryPoint[];
  logs: ApiRequestLog[];

  // Actions
  setTimeRange: (timeRange: TimeRange) => void;
  setSelectedKeyId: (selectedKeyId: 'all' | string) => void;
  setStatusFilter: (statusFilter: StatusFilter) => void;
  setSearchQuery: (searchQuery: string) => void;
  refreshData: () => Promise<void>;
  getFilteredLogs: () => ApiRequestLog[];
  getSummaryMetrics: () => {
    totalRequests: number;
    successRate: number;
    avgLatencyMs: number;
    totalTokens: number;
    peakRpm: number;
  };
  getHttpStatusBreakdown: () => HttpStatusBreakdown;
  getEndpointBreakdown: () => EndpointUsageItem[];
  exportLogsAsCsv: () => string;
}

export const useDeveloperAnalyticsStore = create<DeveloperAnalyticsState>((set, get) => ({
  timeRange: '24h',
  selectedKeyId: 'all',
  statusFilter: 'all',
  searchQuery: '',
  isRefreshing: false,
  telemetryData: generate24hTelemetry(),
  logs: SEED_RECENT_LOGS,

  setTimeRange: (timeRange) => {
    let telemetry: TelemetryPoint[];
    if (timeRange === '24h') telemetry = generate24hTelemetry();
    else if (timeRange === '7d') telemetry = generate7dTelemetry();
    else if (timeRange === '30d') telemetry = generate30dTelemetry();
    else telemetry = generate30dTelemetry(); // 90d mapped

    set({ timeRange, telemetryData: telemetry });
  },

  setSelectedKeyId: (selectedKeyId) => set({ selectedKeyId }),
  setStatusFilter: (statusFilter) => set({ statusFilter }),
  setSearchQuery: (searchQuery) => set({ searchQuery }),

  refreshData: async () => {
    set({ isRefreshing: true });
    await new Promise((resolve) => setTimeout(resolve, 300));
    const currentRange = get().timeRange;
    get().setTimeRange(currentRange);
    set({ isRefreshing: false });
  },

  getFilteredLogs: () => {
    const { logs, selectedKeyId, statusFilter, searchQuery } = get();
    const query = searchQuery.trim().toLowerCase();

    return logs.filter((log) => {
      // Key filter
      if (selectedKeyId !== 'all' && log.keyId !== selectedKeyId) {
        return false;
      }

      // Status filter
      if (statusFilter === '2xx' && (log.statusCode < 200 || log.statusCode >= 300)) {
        return false;
      }
      if (statusFilter === '4xx' && (log.statusCode < 400 || log.statusCode >= 500)) {
        return false;
      }
      if (statusFilter === '5xx' && log.statusCode < 500) {
        return false;
      }

      // Text query
      if (query) {
        const matchesEndpoint = log.endpoint.toLowerCase().includes(query);
        const matchesIp = log.ipAddress.toLowerCase().includes(query);
        const matchesHint = log.keyHint.toLowerCase().includes(query);
        const matchesMethod = log.method.toLowerCase().includes(query);
        if (!matchesEndpoint && !matchesIp && !matchesHint && !matchesMethod) {
          return false;
        }
      }

      return true;
    });
  },

  getSummaryMetrics: () => {
    const { telemetryData } = get();
    if (telemetryData.length === 0) {
      return { totalRequests: 0, successRate: 100, avgLatencyMs: 0, totalTokens: 0, peakRpm: 0 };
    }

    const totalRequests = telemetryData.reduce((acc, p) => acc + p.totalRequests, 0);
    const successful = telemetryData.reduce((acc, p) => acc + p.successfulRequests, 0);
    const totalTokens = telemetryData.reduce((acc, p) => acc + p.tokensUsed, 0);
    const sumLatency = telemetryData.reduce((acc, p) => acc + p.avgLatencyMs * p.totalRequests, 0);
    const avgLatencyMs = totalRequests > 0 ? Math.round(sumLatency / totalRequests) : 45;
    const successRate = totalRequests > 0 ? Math.round((successful / totalRequests) * 1000) / 10 : 100;
    const peakRpm = Math.max(...telemetryData.map((p) => Math.round(p.totalRequests / 60) + 1));

    return {
      totalRequests,
      successRate,
      avgLatencyMs,
      totalTokens,
      peakRpm,
    };
  },

  getHttpStatusBreakdown: () => {
    const { telemetryData } = get();
    const status200 = telemetryData.reduce((acc, p) => acc + p.successfulRequests, 0);
    const status429 = telemetryData.reduce((acc, p) => acc + p.rateLimitedRequests, 0);
    const status5xx = telemetryData.reduce((acc, p) => acc + p.serverErrorRequests, 0);
    const total = status200 + status429 + status5xx;
    const successRate = total > 0 ? Math.round((status200 / total) * 1000) / 10 : 100;

    return {
      status200,
      status429,
      status5xx,
      total,
      successRate,
    };
  },

  getEndpointBreakdown: () => {
    const { logs } = get();
    const map: Record<string, { name: string; endpoint: string; tokens: number; requests: number; color: string }> = {
      chat: { name: 'Chat Studio (LLM)', endpoint: '/api/v1/chat/*', tokens: 0, requests: 0, color: 'text-primary' },
      text: { name: 'AI Writer / Articles', endpoint: '/api/v1/text/*', tokens: 0, requests: 0, color: 'text-accent' },
      images: { name: 'Flux & Image Ops', endpoint: '/api/v1/media/images/*', tokens: 0, requests: 0, color: 'text-emerald-500' },
      voice: { name: 'Voice TTS & Whisper', endpoint: '/api/v1/voice/*', tokens: 0, requests: 0, color: 'text-amber-500' },
      translations: { name: 'CAT Translation', endpoint: '/api/v1/translation/*', tokens: 0, requests: 0, color: 'text-sky-500' },
    };

    logs.forEach((log) => {
      if (log.endpoint.includes('/chat') || log.endpoint.includes('/knowledge')) {
        map.chat.tokens += log.tokensUsed;
        map.chat.requests += 1;
      } else if (log.endpoint.includes('/text')) {
        map.text.tokens += log.tokensUsed;
        map.text.requests += 1;
      } else if (log.endpoint.includes('/media') || log.endpoint.includes('/images')) {
        map.images.tokens += log.tokensUsed;
        map.images.requests += 1;
      } else if (log.endpoint.includes('/voice')) {
        map.voice.tokens += log.tokensUsed;
        map.voice.requests += 1;
      } else if (log.endpoint.includes('/translation')) {
        map.translations.tokens += log.tokensUsed;
        map.translations.requests += 1;
      }
    });

    const totalTokens = Object.values(map).reduce((acc, item) => acc + item.tokens, 0) || 1;

    return Object.entries(map).map(([id, item]) => ({
      id,
      ...item,
      percentage: Math.round((item.tokens / totalTokens) * 100),
    }));
  },

  exportLogsAsCsv: () => {
    const { getFilteredLogs } = get();
    const filtered = getFilteredLogs();

    const headers = ['Request ID', 'Timestamp (ISO)', 'Key ID', 'Key Hint', 'Method', 'Endpoint', 'Status Code', 'Latency (ms)', 'Tokens Used', 'Client IP', 'Error Message'];
    const rows = filtered.map((log) => [
      log.id,
      new Date(log.timestamp).toISOString(),
      log.keyId,
      log.keyHint,
      log.method,
      log.endpoint,
      log.statusCode.toString(),
      log.latencyMs.toString(),
      log.tokensUsed.toString(),
      log.ipAddress,
      log.errorMessage ? `"${log.errorMessage.replace(/"/g, '""')}"` : '',
    ]);

    const csvContent = '\uFEFF' + [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
    return csvContent;
  },
}));
